<template>
    <ValidationObserver
        v-slot="{ invalid }"
        ref="observer"
        tag="div"
    >
        <div class="box-body">
            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-2" v-if="isEdit">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="active"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('active') }}</label>

                            <FormSwitcher
                                v-model="shortDomain.active"
                                :disabled="isLoading || isActiveDisabled"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="redirect url"
                        vid="redirect_url"
                        rules="url"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('redirectUrl') }}</label>

                            <input
                                v-model="shortDomain.redirect_url"
                                type="text"
                                class="form-control"
                                :placeholder="`${$t('enter')} ${$t('redirectUrl')}`"
                                :disabled="isLoading"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>
            </div>

            <div class="row" v-if="!isEdit || availableManual">
                <div class="col-xs-12 col-sm-6 col-lg-2">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="name"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('name') }}</label>

                            <input
                                v-model="shortDomain.name"
                                type="text"
                                class="form-control"
                                :placeholder="`${$t('enter')} ${$t('name')}`"
                                :disabled="isLoading"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="provider"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('shortDomains.provider') }}</label>

                            <FormSelect
                                v-model="shortDomain.provider"
                                :options="providers"
                                :placeholder="`${$t('select')} ${$t('shortDomains.provider')}`"
                                searchable
                                trackBy="id"
                                label="name"
                                closeOnSelect
                                :disabled="isLoading"
                                @input="onChangeProvider"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="account"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('shortDomains.account') }}</label>

                            <FormSelect
                                v-model="shortDomain.account"
                                :options="accounts"
                                :placeholder="`${$t('select')} ${$t('shortDomains.account')}`"
                                searchable
                                trackBy="id"
                                label="name"
                                closeOnSelect
                                :disabled="isLoading"
                                :loading="isLoadingAccounts"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>
            </div>
        </div>

        <div class="box-footer">
            <div class="row">
                <div class="col-xs-8">
                    <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="isLoading || invalid"
                        @click="submitShortDomain"
                    >
                        <i
                            v-if="isLoading"
                            class="fa fa-refresh fa-spin"
                        />
                        <span>{{ isEdit ? 'Update' : 'Create' }}</span>
                    </button>

                    <button
                        class="btn btn-sm btn-default"
                        :disabled="isLoading"
                        @click="
                            $router.push({
                                name: 'short-domains.list',
                            })
                        "
                    >
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import EventBus from '@/event-bus';
import FormSwitcher from '@/components/form/FormSwitcher';

export default {
    components: {
        FormSwitcher,
    },
    props: {
        shortDomainId: {
            type: Number,
            default: undefined,
        },
    },
    data() {
        return {
            isLoading: false,
            shortDomain: {
                name: '',
                active: true,
                redirect_url: '',
                provider: {},
                account: {},
            },
            providers: [],
            accounts: [],
            isLoadingAccounts: false,
            availableManual: false,
        };
    },
    computed: {
        isActiveDisabled() {
            return ['FRESH'].includes(this.shortDomain.status);
        },
        isEdit() {
            return !!this.shortDomainId;
        },
    },
    created() {
        this.fetchShortDomain();
    },
    methods: {
        async fetchShortDomain() {
            try {
                this.isLoading = true;

                const { data: { providers} } = await axios.get(`/short-domains/providers`);
                this.providers = providers;

                if (this.isEdit) {
                    const { data: shortDomain } = await axios.get(`/short-domains/${this.shortDomainId || 0}/data`);
                    this.shortDomain = shortDomain;
                    this.availableManual = shortDomain.account.provider.available_manual;
                    await this.onChangeProvider(shortDomain.provider);
                }

                this.$emit('loaded', this.shortDomain);
            } catch (error) {
                this.is404 = error.response.status === 404;

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoading = false;
            }
        },
        async submitShortDomain() {
            if (!(await this.$refs.observer.validate())) {
                return;
            }

            this.isLoading = true;

            const url = '/short-domains/data/short-domain',
                requestData = {
                    redirectUrl: this.shortDomain.redirect_url,
                };

            if (this.isEdit) {
                requestData.active = this.shortDomain.active;
                requestData.id = this.shortDomain.id;

                if (this.availableManual) {
                    requestData.name = this.shortDomain.name;
                    requestData.accId = this.shortDomain.account.id;
                }
            } else {
                requestData.active = false;
                requestData.name = this.shortDomain.name;
                requestData.accId = this.shortDomain.account.id;
            }

           let self = this;
           const request = await this.isEdit
                ? axios.put(url, requestData)
                : axios.post(url, requestData);

           request
           .then((res) => {
               self.shortDomain = res.data.shortDomain;
               self.onUpdated(res.data.shortDomain.id);
           })
           .catch((res) => {
               const response = res.response.data;
               const message = response.message;
               const errors = response.errors;
               if (errors || message) {
                   errors && self.$refs.observer.setErrors(errors);
                   EventBus.$emit('alertData', {
                       class: 'alert-danger',
                       message,
                       errors,
                   });
               }
           })
           .finally(() => {
               self.isLoading = false;
           });
        },
        async onUpdated(id) {
            if (!id) return;

            await this.$router.push({
                name: 'short-domains.show',
                params: { id:id },
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.isEdit
                    ? this.$t('messages.shortDomain.updated', { id })
                    : this.$t('messages.shortDomain.created', { id }) ,
            });
        },
        async fetchAccounts(providerId) {
            try {
                this.isLoadingAccounts = true;
                const { data: { accounts } } = await axios.get(`/short-domains/provider/${providerId}/accounts`);
                this.accounts = accounts;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoadingAccounts = false;
            }
        },
        async onChangeProvider(provider) {
            await this.fetchAccounts(provider.id);
        },
    },
};
</script>
